/**
 * How a business is named on screen when it has no logo.
 *
 * app.tenant.logo_url is null on almost every business, and there is no upload
 * route yet, so two letters off the name is not the fallback case — it is the
 * case. It gets its own file, and its own test, for that reason.
 *
 * Kept out of the component and out of the hook so the rule is stated once. A
 * mark that reads "SH" on Home and "ST" in the switcher is two businesses to
 * anybody scanning the list.
 */

/**
 * One letter per word, at most two, upper case.
 *
 * ONE PER WORD, NEVER TWO OFF THE FIRST. An owner's businesses share a word far
 * more often than not — "Sharma Traders" and "Sharma Transport" — and the first
 * two letters of the first word make those identical. The first letter of each
 * word tells them apart: ST and ST... which it does not. That collision is
 * real, and it is why `businessCaption` exists below: the mark narrows the
 * field and the caption settles it. Initials alone are never the whole answer.
 *
 * A name with one word gives one letter. A name that is only punctuation or
 * spaces gives an empty string, and the caller draws an empty tile rather than
 * a crash.
 */
export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();

/**
 * The line under a business's name in the switcher.
 *
 * The level always, and the job when there is one. Both already translated by
 * the caller, because the level is an i18n key and the job is the business's
 * own text and this file must not decide which is which.
 */
export const businessCaption = (level: string, job: string | null): string =>
  job === null || job === '' ? level : `${level} · ${job}`;
